import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  use,
} from 'react'
import { IssuerDto } from '../shared/types'
import { IssuersApi } from '../../api/issuers-api'

interface IssuersContextType {
  issuers: IssuerDto[]
  isLoading: boolean
  error: string | null
  refreshIssuers: () => Promise<void>
}

const IssuersContext = createContext<IssuersContextType | undefined>(undefined)

interface IssuersProviderProps {
  children: React.ReactNode
}

export const IssuersProvider: React.FC<IssuersProviderProps> = ({
  children,
}) => {
  const [issuers, setIssuers] = useState<IssuerDto[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const refreshIssuers = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const fetchedIssuers = await IssuersApi.getIssuers()
      // Sort issuers by display name using locale-aware comparison
      const sortedIssuers = [...fetchedIssuers].sort((a, b) =>
        a.displayName.localeCompare(b.displayName)
      )
      setIssuers(sortedIssuers)
    } catch (error) {
      console.error('Error fetching issuers:', error)
      setError('Failed to load issuers. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void refreshIssuers()
  }, [refreshIssuers])

  const contextValue = useMemo(
    () => ({
      issuers,
      isLoading,
      error,
      refreshIssuers,
    }),
    [issuers, isLoading, error, refreshIssuers]
  )

  return <IssuersContext value={contextValue}>{children}</IssuersContext>
}

// eslint-disable-next-line react-refresh/only-export-components
export const useIssuers = () => {
  const context = use(IssuersContext)
  if (context === undefined) {
    throw new Error('useIssuers must be used within an IssuersProvider')
  }
  return context
}
